/** Crow-AMSAA (power-law NHPP) reliability-growth fit for a repairable item's failure
 *  history. Models cumulative failures over CALENDAR time as E[N(t)] = λ·t^β, so a
 *  log-log plot of cumulative failures vs time is a straight line of slope β:
 *    β < 1 → failure rate decreasing (reliability improving);
 *    β ≈ 1 → constant rate (homogeneous Poisson / "random");
 *    β > 1 → failure rate increasing (deteriorating).
 *
 *  This is the parametric, trend-model companion to the non-parametric Laplace test
 *  (see laplaceTrend in temporalDistribution.ts) and is the CORRECT lens when a trend
 *  exists — unlike Weibull-on-gaps, which assumes i.i.d. inter-failure gaps (no trend).
 *
 *  Pure functions — no React/chart deps. */

const MS_PER_DAY = 86400000;

export interface CrowAmsaaFit {
    /** Shape / growth slope (MLE). <1 improving, ≈1 random, >1 deteriorating. */
    beta: number;
    /** Scale (intensity) such that E[N(t)] = lambda · t^beta, t in days from origin. */
    lambda: number;
    /** Asymptotic 95% CI on beta (β̂ ± 1.96·β̂/√n; SE(β̂) ≈ β/√n). */
    betaLower: number;
    betaUpper: number;
    /** R² of the log-log cumulative plot (ln i vs ln tᵢ) — fit quality, sample-size
     *  stable (per the project's goodness-of-fit guidance: R² on the plot, not a GOF test). */
    r2: number;
    /** Number of failures used (inside the observation window, time-from-origin > 0). */
    n: number;
    /** Truncation time T in days (observation-window length). */
    T: number;
    /** Observation-window end (absolute ms) — the anchor the forecast horizon extends from. */
    endMs: number;
    /** Empirical points for the cumulative plot: time-from-origin (days) and the running
     *  failure index i (1-based). x is always > 0. */
    points: {t: number; cum: number}[];
}

/** Plain-English regime read for a Crow-AMSAA beta, mirroring the shape of
 *  weibullDiagnosis so the dialog can render a consistent chip. */
export const crowAmsaaDiagnosis = (beta: number): {label: string; color: string; bgColor: string} => {
    if (beta < 0.9) return {label: 'Improving (reliability growth)', color: '#2e7d32', bgColor: 'rgba(46, 125, 50, 0.1)'};
    if (beta > 1.1) return {label: 'Deteriorating (failure rate rising)', color: '#d32f2f', bgColor: 'rgba(211, 47, 47, 0.1)'};
    return {label: 'Random (roughly constant rate)', color: '#f57c00', bgColor: 'rgba(245, 124, 0, 0.1)'};
};

/** Fit a time-truncated Crow-AMSAA model. `failureTimestamps` are absolute ms;
 *  `originMs`/`endMs` bound the observation window (use the analysis date range — an
 *  origin strictly before the first failure keeps every time-from-origin > 0, avoiding
 *  the ln(T/tᵢ) singularity at tᵢ = 0). Returns null below `minFailures` (β is unstable
 *  on tiny samples) or when the window is degenerate.
 *
 *  MLE (Type-I / time-terminated): β̂ = n / Σ ln(T/tᵢ), λ̂ = n / T^β̂. */
export const fitCrowAmsaa = (
    failureTimestamps: number[],
    originMs: number,
    endMs: number,
    minFailures = 8,
): CrowAmsaaFit | null => {
    const T = (endMs - originMs) / MS_PER_DAY;
    if (!(T > 0)) return null;
    // Time-from-origin in days, in-window and strictly positive, ascending.
    const t = failureTimestamps
        .filter((ms) => !isNaN(ms) && ms > originMs && ms <= endMs)
        .map((ms) => (ms - originMs) / MS_PER_DAY)
        .sort((a, b) => a - b);
    const n = t.length;
    if (n < minFailures) return null;

    let sumLn = 0;
    for (const ti of t) sumLn += Math.log(T / ti);
    if (!(sumLn > 0)) return null; // degenerate (all failures at the truncation instant)
    const beta = n / sumLn;
    const lambda = n / Math.pow(T, beta);

    // Asymptotic normal CI: SE(β̂) ≈ β̂/√n.
    const se = beta / Math.sqrt(n);
    const betaLower = Math.max(0, beta - 1.96 * se);
    const betaUpper = beta + 1.96 * se;

    // R² of the log-log cumulative plot: regress y = ln(i) on x = ln(tᵢ), i = 1..n.
    const points: {t: number; cum: number}[] = t.map((ti, i) => ({t: ti, cum: i + 1}));
    const xs = t.map((ti) => Math.log(ti));
    const ys = points.map((p) => Math.log(p.cum));
    const r2 = rSquared(xs, ys);

    return {beta, lambda, betaLower, betaUpper, r2, n, T, endMs, points};
};

export interface CrowAmsaaForecast {
    /** Expected ADDITIONAL failures in [T, T+horizon], point estimate. */
    expectedFailures: number;
    /** β-uncertainty band on expectedFailures (from the β 95% CI endpoints, λ re-pinned
     *  to (T, n) at each β so all curves agree at the observation end and fan into the
     *  future). lo/hi may invert around β=1; returned already min/max-ordered. */
    lowFailures: number;
    highFailures: number;
    /** Expected days to the next failure after T — the horizon at which one more failure
     *  is expected: (T^β + 1/λ)^(1/β) − T. */
    daysToNextFailure: number;
}

/** Forecast future failures under the fitted NHPP. `horizonDays` is the look-ahead beyond
 *  the observation end T. Pure extrapolation — assumes the fitted trend continues. */
export const crowAmsaaForecast = (fit: CrowAmsaaFit, horizonDays: number): CrowAmsaaForecast => {
    const {beta, lambda, betaLower, betaUpper, T, n} = fit;
    const h = Math.max(0, horizonDays);
    const expectedFailures = lambda * (Math.pow(T + h, beta) - Math.pow(T, beta));
    // β-CI band: re-pin λ = n / T^b so every curve passes through (T, n), then extrapolate.
    const atBeta = (b: number): number => (n / Math.pow(T, b)) * (Math.pow(T + h, b) - Math.pow(T, b));
    const a = atBeta(betaLower);
    const c = atBeta(betaUpper);
    const daysToNextFailure = Math.pow(Math.pow(T, beta) + 1 / lambda, 1 / beta) - T;
    return {expectedFailures, lowFailures: Math.min(a, c), highFailures: Math.max(a, c), daysToNextFailure};
};

/** Coefficient of determination for an ordinary least-squares line y = a + b·x. */
function rSquared(xs: number[], ys: number[]): number {
    const n = xs.length;
    if (n < 2) return 0;
    const meanX = xs.reduce((s, v) => s + v, 0) / n;
    const meanY = ys.reduce((s, v) => s + v, 0) / n;
    let sxx = 0;
    let sxy = 0;
    let syy = 0;
    for (let i = 0; i < n; i++) {
        const dx = xs[i] - meanX;
        const dy = ys[i] - meanY;
        sxx += dx * dx;
        sxy += dx * dy;
        syy += dy * dy;
    }
    if (sxx === 0 || syy === 0) return 0;
    const r = sxy / Math.sqrt(sxx * syy);
    return r * r;
}
